"use client";

import { useEffect, useRef, useState } from "react";

import { Command, Search } from "lucide-react";
import { useRouter } from "next/navigation";

import { AIEnhancedSearch } from "@/components/ai/ai-enhanced-search";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { AlgoliaSearch } from "./algolia-search";

interface GlobalSearchProps {
  variant?: "header" | "page";
  placeholder?: string;
  useAISearch?: boolean; // Toggle between Algolia and AI-enhanced search
}

export function GlobalSearch({
  variant = "header",
  placeholder = "Search patients, documents...",
  useAISearch = true,
}: GlobalSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [_searchQuery, setSearchQuery] = useState("");
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      // Escape to close
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handlePatientSelect = (patient: any) => {
    setIsOpen(false);
    setSearchQuery("");
    router.push(`/patients/${patient.id}`);
  };

  const handleDocumentSelect = (document: any) => {
    setIsOpen(false);
    setSearchQuery("");
    // Navigate to document view or patient documents tab
    router.push(`/patients/${document.patientId}?tab=documents&docId=${document.id}`);
  };

  if (variant === "header") {
    return (
      <>
        {/* Header Search Trigger */}
        <div className="relative w-full max-w-full sm:max-w-2xl">
          <Button
            variant="outline"
            className="h-9 w-full justify-start border-gray-300 bg-white text-xs font-normal text-muted-foreground shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 hover:shadow-md focus-visible:ring-2 focus-visible:ring-blue-500 sm:h-10 sm:text-sm md:h-11 md:text-base"
            onClick={() => setIsOpen(true)}
          >
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-gray-500 sm:h-4 sm:w-4" />
            <span className="truncate">{placeholder}</span>
            <div className="ml-auto flex shrink-0 items-center space-x-1">
              <kbd className="pointer-events-none hidden items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-1.5 py-0.5 font-mono text-[9px] font-medium text-gray-600 shadow-sm sm:inline-flex sm:px-2 sm:py-1 sm:text-[10px]">
                <Command className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                <span>K</span>
              </kbd>
            </div>
          </Button>
        </div>

        {/* Search Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] max-w-5xl overflow-hidden p-0 sm:w-[90vw] md:w-[85vw]">
            <DialogHeader className="border-b border-gray-200 px-3 py-3 sm:px-4 sm:py-4 md:px-6">
              <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900 sm:gap-3 sm:text-lg">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 sm:h-10 sm:w-10">
                  <Search className="h-4 w-4 text-blue-600 sm:h-5 sm:w-5" />
                </div>
                <span className="text-sm sm:text-base md:text-lg">Search Healthcare Records</span>
              </DialogTitle>
            </DialogHeader>
            <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-4">
              {useAISearch ? (
                <AIEnhancedSearch
                  onPatientSelect={handlePatientSelect}
                  onDocumentSelect={handleDocumentSelect}
                  placeholder="Ask a question or search with natural language..."
                  defaultTab="all"
                />
              ) : (
                <AlgoliaSearch
                  onPatientSelect={handlePatientSelect}
                  onDocumentSelect={handleDocumentSelect}
                  placeholder="Search patients, documents, procedures..."
                  showFilters={true}
                  defaultTab="all"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Page variant - render search directly
  return (
    <div className="mx-auto w-full max-w-full sm:max-w-2xl md:max-w-4xl">
      {useAISearch ? (
        <AIEnhancedSearch
          onPatientSelect={handlePatientSelect}
          onDocumentSelect={handleDocumentSelect}
          placeholder={placeholder}
          defaultTab="all"
        />
      ) : (
        <AlgoliaSearch
          onPatientSelect={handlePatientSelect}
          onDocumentSelect={handleDocumentSelect}
          placeholder={placeholder}
          showFilters={true}
          defaultTab="all"
        />
      )}
    </div>
  );
}

// Quick search component for patient selection contexts
export function PatientQuickSearch({
  onSelect,
  placeholder = "Search patients...",
}: {
  onSelect: (patient: any) => void;
  placeholder?: string;
}) {
  return (
    <AlgoliaSearch onPatientSelect={onSelect} placeholder={placeholder} showFilters={false} defaultTab="patients" />
  );
}

// Document search component
export function DocumentSearch({
  onSelect,
  patientId: _patientId,
  placeholder = "Search documents...",
}: {
  onSelect: (document: any) => void;
  patientId?: string;
  placeholder?: string;
}) {
  return (
    <AlgoliaSearch onDocumentSelect={onSelect} placeholder={placeholder} showFilters={true} defaultTab="documents" />
  );
}
